import React, { useState, useCallback, useEffect } from 'react';
import { FiSave, FiXCircle, FiUserPlus } from 'react-icons/fi';
import InputMask from 'react-input-mask';
import Swal from 'sweetalert2';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import styles from './ViewVeiculo.module.css';

const initialOperadorState = {
    username: '', password: '', confirmPassword: '',
    nome_completo: '', cpf: '', numero_habilitacao: ''
};

const EditVeiculoPopup = ({ vehicle, onClose, onSave }) => {
    
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [informarVencimentos, setInformarVencimentos] = useState(false);

    const [operadores, setOperadores] = useState([]);
    const [showOperadorModal, setShowOperadorModal] = useState(false);
    const [novoOperador, setNovoOperador] = useState(initialOperadorState);
    const [operadorModalErrors, setOperadorModalErrors] = useState({});
    const [isSavingOperador, setIsSavingOperador] = useState(false);
    
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };
    
    const fetchOperadores = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/operadores`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setOperadores(response.data);
        } catch (error) {
            console.error("Erro ao buscar operadores:", error);
            Swal.fire('Erro!', 'Não foi possível carregar a lista de operadores.', 'error');
        }
    }, []);

    const handleNovoOperadorChange = (e) => {
        const { name, value } = e.target;
        setNovoOperador(prev => ({ ...prev, [name]: value }));
        if (operadorModalErrors?.[name]) {
            setOperadorModalErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const handleNovoOperadorSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};
        if (!novoOperador.nome_completo) newErrors.nome_completo = "Nome completo é obrigatório.";
        if (!novoOperador.username) newErrors.username = "Nome de usuário é obrigatório.";
        if (!novoOperador.password) newErrors.password = "Senha é obrigatória.";
        if (novoOperador.password?.length < 6) newErrors.password = "Senha deve ter no mínimo 6 caracteres.";
        if (novoOperador.password !== novoOperador.confirmPassword) newErrors.confirmPassword = "As senhas não coincidem.";
        if (!/^\d{11}$/.test(novoOperador.cpf?.replace(/\D/g, ''))) newErrors.cpf = "CPF inválido.";
        if (!/^\d+$/.test(novoOperador.numero_habilitacao)) newErrors.numero_habilitacao = "CNH deve conter apenas números.";

        if (Object.keys(newErrors).length > 0) {
            setOperadorModalErrors(newErrors);
            return;
        }

        setIsSavingOperador(true);
        setOperadorModalErrors({});

        try {
            const token = localStorage.getItem('token');
            const payload = {
                username: novoOperador.username,
                password: novoOperador.password,
                tipo_usuario: 'operador',
                nome_completo: novoOperador.nome_completo,
                cpf: novoOperador.cpf?.replace(/\D/g, ''),
                numero_habilitacao: novoOperador.numero_habilitacao,
            };

            const response = await axios.post(`${API_BASE_URL}/api/usuarios`, payload, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const newUserId = response.data.userId;

            Swal.fire('Sucesso!', 'Operador cadastrado com sucesso!', 'success');

            await fetchOperadores(); 
            
            if (newUserId) {
                setFormData(prev => ({ ...prev, operador_id: newUserId })); 
            }

            setShowOperadorModal(false);
            setNovoOperador(initialOperadorState);

        } catch (error) {
            console.error('Erro ao cadastrar operador:', error);
            const errorMessage = error.response?.data?.error || 'Não foi possível cadastrar o operador.';
            setOperadorModalErrors({ api: errorMessage });
        } finally {
            setIsSavingOperador(false);
        }
    };
    
    useEffect(() => {
        if (vehicle) {
            const hasVencimentos = !!(vehicle.vencimentoAET || vehicle.vencimento_aet_estadual || vehicle.vencimentoCronotacografo || vehicle.vencimentoDocumentos);
            setInformarVencimentos(hasVencimentos);

            setFormData({
                ...vehicle,
                operador_id: vehicle.operador_id || '', 
                capacidadeTanque: vehicle.capacidadeTanque || '',
                quilometragem: vehicle.quilometragem === null || vehicle.quilometragem === undefined || vehicle.quilometragem === '' ? 0 : vehicle.quilometragem,
                horimetro: vehicle.horimetro === null || vehicle.horimetro === undefined || vehicle.horimetro === '' ? 0 : vehicle.horimetro,
                codigo_cc: vehicle.codigo_cc || '',
                potenciaMotor: vehicle.potenciaMotor || '',
                vencimentoAET: formatDateForInput(vehicle.vencimentoAET),
                vencimento_aet_estadual: formatDateForInput(vehicle.vencimento_aet_estadual),
                vencimentoCronotacografo: formatDateForInput(vehicle.vencimentoCronotacografo),
                vencimentoDocumentos: formatDateForInput(vehicle.vencimentoDocumentos),
                tipo_colheita: vehicle.tipo_colheita || '',
                limite_manutencao: vehicle.limite_manutencao || '',
                tipo_limite_manutencao: vehicle.tipo_limite_manutencao || '',
            });
        }
        fetchOperadores();
    }, [vehicle, fetchOperadores]);

    useEffect(() => {
        const veiculoSemCombustivel = ['SEMIRREBOQUE', 'MAQUINA'];
        const veiculoSemPlaca = ['MAQUINA', 'TRATOR'];

        if (veiculoSemCombustivel.includes(formData.tipoVeiculo)) {
            setFormData(prev => ({
                ...prev,
                potenciaMotor: prev.tipoVeiculo === 'SEMIRREBOQUE' ? '' : prev.potenciaMotor,
                tipoCombustivel: '',
                capacidadeTanque: '',
            }));
        }

        if (veiculoSemPlaca.includes(formData.tipoVeiculo)) {
            setFormData(prev => ({
                ...prev,
                placa: '',
                renavam: '',
            }));
        }
        
        if (formData.tipoVeiculo !== 'MAQUINA' && formData.tipoVeiculo) {
            setFormData(prev => ({ ...prev, horimetro: 0 }));
        }
    }, [formData.tipoVeiculo]);


    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        const uppercaseFields = ['placa', 'chassi', 'tipoVeiculo', 'tipoCombustivel', 'tipo_colheita'];
        const finalValue = uppercaseFields.includes(name) ? value.toUpperCase() : value;
        
        const updateValue = name === 'operador_id' && value === '' ? null : finalValue; 
        
        setFormData(prev => ({ ...prev, [name]: updateValue }));
        
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    }, [errors]);

    const handleVencimentosCheckbox = useCallback((e) => {
        const { checked } = e.target;
        setInformarVencimentos(checked);
        if (!checked) {
            setFormData(prev => ({
                ...prev,
                vencimentoAET: '',
                vencimento_aet_estadual: '',
                vencimentoCronotacografo: '',
                vencimentoDocumentos: ''
            }));
        }
    }, []);

    const validateForm = useCallback(() => {
        const newErrors = {};
        const currentYear = new Date().getFullYear();
        const veiculoSemPlaca = ['MAQUINA', 'TRATOR'];
        const veiculoSemCombustivel = ['SEMIRREBOQUE', 'MAQUINA'];
        const tiposColheitaValidos = ['HARVESTER', 'SKIDDER', 'FORWARDER', 'TRATOR GUINCHO', 'TRATOR', 'OUTRO'];

        if (!formData.tipoVeiculo) newErrors.tipoVeiculo = 'Tipo de veículo é obrigatório.';
        if (!formData.marca) newErrors.marca = 'Marca é obrigatória.';
        if (!formData.modelo) newErrors.modelo = 'Modelo é obrigatório.';
        if (formData.anoFabricacao && (+formData.anoFabricacao < 1900 || +formData.anoFabricacao > currentYear + 1)) newErrors.anoFabricacao = 'Ano de Fabricação inválido.';
        if (formData.anoModelo && (+formData.anoModelo < +formData.anoFabricacao)) newErrors.anoModelo = 'Ano Modelo não pode ser anterior à fabricação.';
        if (formData.chassi && formData.chassi.length !== 17) newErrors.chassi = 'Chassi deve ter 17 caracteres.';
        if (!formData.cor) newErrors.cor = 'Cor é obrigatória.';
        
        if (formData.tipo_colheita && !tiposColheitaValidos.includes(formData.tipo_colheita)) {
            newErrors.tipo_colheita = 'Função na Colheita inválida.';
        }
        
        if (formData.tipoVeiculo === 'MAQUINA') {
            if (formData.horimetro !== '' && formData.horimetro !== null && +formData.horimetro < 0) newErrors.horimetro = 'Horímetro inválido.';
        } else {
            if (formData.quilometragem !== '' && formData.quilometragem !== null && +formData.quilometragem < 0) newErrors.quilometragem = 'Quilometragem inválida.';
        }

        if (!veiculoSemPlaca.includes(formData.tipoVeiculo)) {
            if (!formData.placa) newErrors.placa = 'Placa é obrigatória.';
            if (formData.renavam && formData.renavam.replace(/_/g, '').length !== 11) newErrors.renavam = 'RENAVAM deve ter 11 dígitos.';
        }
        
        if (!veiculoSemCombustivel.includes(formData.tipoVeiculo)) {
            if (!formData.tipoCombustivel) newErrors.tipoCombustivel = 'Tipo de Combustível é obrigatório.';
            if (formData.capacidadeTanque && +formData.capacidadeTanque <= 0) newErrors.capacidadeTanque = 'Capacidade do Tanque inválida.';
            if (formData.potenciaMotor && formData.potenciaMotor.length > 50) newErrors.potenciaMotor = 'Potência do Motor deve ter no máximo 50 caracteres.';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            Swal.fire('Erro de Validação', 'Por favor, corrija os campos destacados.', 'error');
            return;
        }
        
        setIsLoading(true);

        const dataToSubmit = {
            ...formData,
            quilometragem: (formData.quilometragem === '' || formData.quilometragem === null || formData.quilometragem === undefined) ? 0 : Number(formData.quilometragem),
            horimetro: (formData.horimetro === '' || formData.horimetro === null || formData.horimetro === undefined) ? 0 : Number(formData.horimetro)
        };

        try {
            const token = localStorage.getItem('token');
            
            await axios.put(`${API_BASE_URL}/api/veiculos/${dataToSubmit.id}`, dataToSubmit, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            Swal.fire('Sucesso!', 'Veículo atualizado com sucesso!', 'success');
            onSave();
        } catch (error) {
            console.error('Erro ao atualizar veículo:', error);
            const errorMessage = error.response?.data?.error || 'Não foi possível atualizar o veículo.';
            Swal.fire('Erro!', errorMessage, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    if (!vehicle) {
        return null;
    }

    const getInputClassName = (fieldName) => `${styles.formInput} ${errors[fieldName] ? styles.inputError : ''}`;
    const getSelectClassName = (fieldName) => `${styles.formSelect} ${errors[fieldName] ? styles.inputError : ''}`;
    const getTextareaClassName = (fieldName) => `${styles.formTextarea} ${errors[fieldName] ? styles.inputError : ''}`;

    const veiculoSemPlaca = ['MAQUINA', 'TRATOR'];
    const veiculoSemCombustivel = ['SEMIRREBOQUE', 'MAQUINA'];

    return (
        <div className={styles.popupOverlay}>
            <div className={styles.popupContent}>
                <header className={styles.popupHeader}>
                    <h2>Editar Veículo: {vehicle.marca} {vehicle.modelo}</h2>
                    <button onClick={onClose} className={styles.closePopupBtn}>&times;</button>
                </header>
                <form onSubmit={handleSubmit} className={styles.popupForm} noValidate>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label htmlFor="tipoVeiculo">Tipo de Veículo *</label>
                            <select id="tipoVeiculo" name="tipoVeiculo" value={formData.tipoVeiculo || ''} onChange={handleChange} className={getSelectClassName('tipoVeiculo')}>
                                <option value="">Selecione...</option>
                                <option value="CARRO">CARRO</option>
                                <option value="MOTO">MOTO</option>
                                <option value="CAMINHAO">CAMINHÃO</option>
                                <option value="TRATOR">TRATOR</option>
                                <option value="MAQUINA">MÁQUINA</option>
                                <option value="SEMIRREBOQUE">SEMIRREBOQUE</option>
                            </select>
                            {errors.tipoVeiculo && <span className={styles.errorMessage}>{errors.tipoVeiculo}</span>}
                        </div>
                        
                        <div className={styles.formGroup}>
                            <label htmlFor="tipo_colheita">Função na Colheita (Opcional)</label>
                            <select 
                                id="tipo_colheita" 
                                name="tipo_colheita" 
                                value={formData.tipo_colheita || ''} 
                                onChange={handleChange} 
                                className={getSelectClassName('tipo_colheita')}
                            >
                                <option value="">Nenhuma...</option>
                                <option value="HARVESTER">HARVESTER (Corte)</option>
                                <option value="SKIDDER">SKIDDER (Arrasto)</option>
                                <option value="FORWARDER">FORWARDER (Baldeio)</option>
                                <option value="TRATOR GUINCHO">TRATOR GUINCHO</option>
                                <option value="TRATOR">TRATOR</option>
                                <option value="OUTRO">OUTRO (MÁQUINA)</option>
                            </select>
                            {errors.tipo_colheita && <span className={styles.errorMessage}>{errors.tipo_colheita}</span>}
                        </div>
                        
                        <div className={styles.formGroup}>
                            <label htmlFor="marca">Marca *</label>
                            <input type="text" id="marca" name="marca" value={formData.marca || ''} onChange={handleChange} className={getInputClassName('marca')} />
                            {errors.marca && <span className={styles.errorMessage}>{errors.marca}</span>}
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="modelo">Modelo *</label>
                            <input type="text" id="modelo" name="modelo" value={formData.modelo || ''} onChange={handleChange} className={getInputClassName('modelo')} />
                            {errors.modelo && <span className={styles.errorMessage}>{errors.modelo}</span>}
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="anoFabricacao">Ano Fabricação *</label>
                            <input type="number" id="anoFabricacao" name="anoFabricacao" value={formData.anoFabricacao || ''} onChange={handleChange} className={getInputClassName('anoFabricacao')} />
                            {errors.anoFabricacao && <span className={styles.errorMessage}>{errors.anoFabricacao}</span>}
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="anoModelo">Ano Modelo *</label>
                            <input type="number" id="anoModelo" name="anoModelo" value={formData.anoModelo || ''} onChange={handleChange} className={getInputClassName('anoModelo')} />
                            {errors.anoModelo && <span className={styles.errorMessage}>{errors.anoModelo}</span>}
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="cor">Cor *</label>
                            <input type="text" id="cor" name="cor" value={formData.cor || ''} onChange={handleChange} className={getInputClassName('cor')} />
                            {errors.cor && <span className={styles.errorMessage}>{errors.cor}</span>}
                        </div>
                        <div className={`${styles.formGroup} ${formData.tipoVeiculo === 'SEMIRREBOQUE' ? styles.disabledContent : ''}`}>
                            <label htmlFor="potenciaMotor">Potência do Motor</label>
                            <input 
                                type="text" 
                                id="potenciaMotor" 
                                name="potenciaMotor" 
                                value={formData.potenciaMotor || ''} 
                                onChange={handleChange} 
                                className={getInputClassName('potenciaMotor')} 
                                maxLength="50"
                                disabled={formData.tipoVeiculo === 'SEMIRREBOQUE'}
                            />
                            {errors.potenciaMotor && <span className={styles.errorMessage}>{errors.potenciaMotor}</span>}
                        </div>

                        {!veiculoSemPlaca.includes(formData.tipoVeiculo) && (
                            <>
                                <div className={styles.formGroup}>
                                   <label htmlFor="placa">Placa *</label>
                                   <input type="text" id="placa" name="placa" value={formData.placa || ''} onChange={handleChange} className={getInputClassName('placa')} />
                                   {errors.placa && <span className={styles.errorMessage}>{errors.placa}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                     <label htmlFor="renavam">RENAVAM *</label>
                                     <InputMask mask="99999999999" maskChar={null} id="renavam" name="renavam" value={formData.renavam || ''} onChange={handleChange} className={getInputClassName('renavam')} />
                                     {errors.renavam && <span className={styles.errorMessage}>{errors.renavam}</span>}
                                </div>
                            </>
                        )}
                        <div className={`${styles.formGroup} ${styles.fullWidthDetail}`}>
                            <label htmlFor="chassi">Chassi *</label>
                            <input type="text" id="chassi" name="chassi" value={formData.chassi || ''} onChange={handleChange} className={getInputClassName('chassi')} maxLength="17" />
                            {errors.chassi && <span className={styles.errorMessage}>{errors.chassi}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="operador_id">Operador Responsável</label>
                            <div className={styles.operadorSelectWrapper}>
                                <select 
                                    id="operador_id" 
                                    name="operador_id" 
                                    value={formData.operador_id || ''} 
                                    onChange={handleChange} 
                                    className={getSelectClassName('operador_id')}
                                >
                                    <option value="">Nenhum operador</option>
                                    {operadores.map(operador => (
                                        <option key={operador.id} value={operador.id}>
                                            {operador.nome_completo}
                                        </option>
                                    ))}
                                </select>
                                <button type="button" className={styles.btnAddOperador} onClick={() => setShowOperadorModal(true)} title="Adicionar Novo Operador">
                                    <FiUserPlus />
                                </button>
                            </div>
                            {errors.operador_id && <span className={styles.errorMessage}>{errors.operador_id}</span>}
                        </div>
                    </div>
                    
                    <div className={`${styles.formGroup} ${styles.fullWidthDetail}`}>
                        <div className={styles.sectionHeader}>
                            <h3>Controle de Vencimentos</h3>
                            <div className={styles.checkboxGroup}>
                                <input type="checkbox" id="informarVencimentosEdit" name="informarVencimentos" checked={informarVencimentos} onChange={handleVencimentosCheckbox} />
                                <label htmlFor="informarVencimentosEdit">Informar vencimentos?</label>
                            </div>
                        </div>
                    </div>
                    <div className={`${styles.formGrid} ${!informarVencimentos ? styles.disabledContent : ''}`}>
                        <div className={styles.formGroup}>
                            <label htmlFor="vencimentoAET">Vencimento AET (Nacional)</label>
                            <input type="date" id="vencimentoAET" name="vencimentoAET" value={formData.vencimentoAET || ''} onChange={handleChange} className={getInputClassName('vencimentoAET')} disabled={!informarVencimentos} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="vencimento_aet_estadual">Vencimento AET (Estadual)</label>
                            <input type="date" id="vencimento_aet_estadual" name="vencimento_aet_estadual" value={formData.vencimento_aet_estadual || ''} onChange={handleChange} className={getInputClassName('vencimento_aet_estadual')} disabled={!informarVencimentos} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="vencimentoCronotacografo">Vencimento Cronotacógrafo</label>
                            <input type="date" id="vencimentoCronotacografo" name="vencimentoCronotacografo" value={formData.vencimentoCronotacografo || ''} onChange={handleChange} className={getInputClassName('vencimentoCronotacografo')} disabled={!informarVencimentos} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="vencimentoDocumentos">Vencimento Licenças/Documentos</label>
                            <input type="date" id="vencimentoDocumentos" name="vencimentoDocumentos" value={formData.vencimentoDocumentos || ''} onChange={handleChange} className={getInputClassName('vencimentoDocumentos')} disabled={!informarVencimentos} />
                        </div>
                    </div>

                    <div className={styles.formGrid}>
                        {formData.tipoVeiculo === 'MAQUINA' ? (
                            <div className={styles.formGroup}>
                                <label htmlFor="horimetro">Horímetro</label>
                                <input type="number" step="any" id="horimetro" name="horimetro" value={formData.horimetro || ''} onChange={handleChange} className={getInputClassName('horimetro')} min="0" />
                                {errors.horimetro && <span className={styles.errorMessage}>{errors.horimetro}</span>}
                            </div>
                        ) : (
                            <div className={styles.formGroup}>
                               <label htmlFor="quilometragem">Quilometragem (KM)</label>
                               <input type="number" step="any" id="quilometragem" name="quilometragem" value={formData.quilometragem || ''} onChange={handleChange} className={getInputClassName('quilometragem')} min="0" />
                               {errors.quilometragem && <span className={styles.errorMessage}>{errors.quilometragem}</span>}
                            </div>
                        )}
                        
                        <div className={`${styles.formGroup} ${veiculoSemCombustivel.includes(formData.tipoVeiculo) ? styles.disabledContent : ''}`}>
                            <label htmlFor="tipoCombustivel">Combustível *</label>
                            <select 
                                id="tipoCombustivel" 
                                name="tipoCombustivel" 
                                value={formData.tipoCombustivel || ''} 
                                onChange={handleChange} 
                                className={getSelectClassName('tipoCombustivel')}
                                disabled={veiculoSemCombustivel.includes(formData.tipoVeiculo)}
                            >
                                <option value="">Selecione...</option>
                                <option value="GASOLINA">GASOLINA</option>
                                <option value="ETANOL">ETANOL</option>
                                <option value="DIESEL">DIESEL</option>
                                <option value="FLEX">FLEX (GASOLINA/ETANOL)</option>
                            </select>
                            {errors.tipoCombustivel && <span className={styles.errorMessage}>{errors.tipoCombustivel}</span>}
                        </div>
                        <div className={`${styles.formGroup} ${veiculoSemCombustivel.includes(formData.tipoVeiculo) ? styles.disabledContent : ''}`}>
                            <label htmlFor="capacidadeTanque">Tanque (Litros) *</label>
                            <input 
                                type="number" 
                                step="any" 
                                id="capacidadeTanque" 
                                name="capacidadeTanque" 
                                value={formData.capacidadeTanque || ''} 
                                onChange={handleChange} 
                                className={getInputClassName('capacidadeTanque')} 
                                min="0"
                                disabled={veiculoSemCombustivel.includes(formData.tipoVeiculo)}
                            />
                            {errors.capacidadeTanque && <span className={styles.errorMessage}>{errors.capacidadeTanque}</span>}
                        </div>
                        
                        <div className={styles.formGroup}>
                            <label htmlFor="limite_manutencao">Limite Manutenção</label>
                            <input type="number" id="limite_manutencao" name="limite_manutencao" value={formData.limite_manutencao || ''} onChange={handleChange} className={getInputClassName('limite_manutencao')} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="tipo_limite_manutencao">Tipo Limite</label>
                            <select id="tipo_limite_manutencao" name="tipo_limite_manutencao" value={formData.tipo_limite_manutencao || ''} onChange={handleChange} className={getSelectClassName('tipo_limite_manutencao')}>
                                <option value="">Selecione...</option>
                                <option value="quilometragem">Quilometragem</option>
                                <option value="horimetro">Horímetro</option>
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="codigo_cc">Código CC</label>
                            <input type="text" id="codigo_cc" name="codigo_cc" value={formData.codigo_cc || ''} onChange={handleChange} className={getInputClassName('codigo_cc')} />
                            {errors.codigo_cc && <span className={styles.errorMessage}>{errors.codigo_cc}</span>}
                        </div>
                        
                        <div className={`${styles.formGroup} ${styles.fullWidthDetail}`}>
                            <label htmlFor="observacoes">Observações</label>
                            <textarea id="observacoes" name="observacoes" value={formData.observacoes || ''} onChange={handleChange} className={getTextareaClassName('observacoes')} rows="4"></textarea>
                        </div>
                    </div>
                    
                    <div className={styles.popupActions}>
                        <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onClose} disabled={isLoading}>
                            Cancelar
                        </button>
                        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={isLoading}>
                            <FiSave />
                            {isLoading ? 'Salvando...' : 'Salvar Alterações'}
                        </button>
                    </div>
                </form>
            </div>
            
            {showOperadorModal && (
                <div className={styles.operadorModalOverlay}>
                    <div className={styles.operadorModalContainer}>
                        <header className={styles.operadorModalHeader}>
                            <h3>Cadastrar Novo Operador</h3>
                            <button className={styles.operadorModalCloseBtn} onClick={() => setShowOperadorModal(false)}>
                                <FiXCircle />
                            </button>
                        </header>
                        <div className={styles.operadorModalBody}>
                            <form onSubmit={handleNovoOperadorSubmit} id="novo-operador-form-edit" noValidate>
                                {operadorModalErrors?.api && <div className={styles.modalErrorMessage}>{operadorModalErrors.api}</div>}
                                <div className={`${styles.formGroup} ${styles.fullWidthDetail}`}>
                                    <label htmlFor="nome_completo">Nome Completo *</label>
                                    <input type="text" id="nome_completo" name="nome_completo" value={novoOperador.nome_completo} onChange={handleNovoOperadorChange} className={getInputClassName('nome_completo')} required />
                                    {operadorModalErrors?.nome_completo && <span className={styles.errorMessage}>{operadorModalErrors.nome_completo}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="username">Nome de Usuário *</label>
                                    <input type="text" id="username" name="username" value={novoOperador.username} onChange={handleNovoOperadorChange} className={getInputClassName('username')} required />
                                    {operadorModalErrors?.username && <span className={styles.errorMessage}>{operadorModalErrors.username}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="cpf">CPF *</label>
                                    <InputMask mask="999.999.999-99" id="cpf" name="cpf" value={novoOperador.cpf} onChange={handleNovoOperadorChange} className={getInputClassName('cpf')} required />
                                    {operadorModalErrors?.cpf && <span className={styles.errorMessage}>{operadorModalErrors.cpf}</span>}
                                </div>
                                <div className={`${styles.formGroup} ${styles.fullWidthDetail}`}>
                                    <label htmlFor="numero_habilitacao">Nº da Habilitação (CNH) *</label>
                                    <input type="text" id="numero_habilitacao" name="numero_habilitacao" value={novoOperador.numero_habilitacao} onChange={handleNovoOperadorChange} className={getInputClassName('numero_habilitacao')} required />
                                    {operadorModalErrors?.numero_habilitacao && <span className={styles.errorMessage}>{operadorModalErrors.numero_habilitacao}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="password">Senha *</label>
                                    <input type="password" id="password" name="password" value={novoOperador.password} onChange={handleNovoOperadorChange} className={getInputClassName('password')} required />
                                    {operadorModalErrors?.password && <span className={styles.errorMessage}>{operadorModalErrors.password}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="confirmPassword">Confirmar Senha *</label>
                                    <input type="password" id="confirmPassword" name="confirmPassword" value={novoOperador.confirmPassword} onChange={handleNovoOperadorChange} className={getInputClassName('confirmPassword')} required />
                                    {operadorModalErrors?.confirmPassword && <span className={styles.errorMessage}>{operadorModalErrors.confirmPassword}</span>}
                                </div>
                            </form>
                        </div>
                        <footer className={styles.operadorModalFooter}>
                            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setShowOperadorModal(false)} disabled={isSavingOperador}>
                                Cancelar
                            </button>
                            <button type="submit" form="novo-operador-form-edit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={isSavingOperador}>
                                {isSavingOperador ? <span className={styles.spinner}></span> : <FiSave className={styles.btnIcon} />}
                                {isSavingOperador ? 'Salvando...' : 'Salvar Operador'}
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditVeiculoPopup;